import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

/**
 * Focus management on every route change (design.md §10): after
 * navigation, focus lands on the page's h1 (every page has exactly one,
 * from PageHeader) or, failing that, on <main>, so a screen reader hears
 * where it is; the document title names the page too. Not on the first
 * render — the browser's own start is right there — and never because the
 * product name arrived from /v1/me a second later: that re-ran the focus
 * move and stole the person's place, and on a fresh load with a hash it
 * pulled the page back to the top (gairm/lms#203, walk 5). The title
 * follows the name; only the path moves focus.
 *
 * A lazy page (the docs shelf) arrives after the path changed: the h1 is
 * awaited, not assumed — <main> is watched until it appears — so the
 * title and the focus are the page's, not the fallback's.
 */
export function RouteFocus({ productName }: { productName: string }) {
  const { pathname } = useLocation();
  const first = useRef(true);
  const nameRef = useRef(productName);
  useEffect(() => {
    nameRef.current = productName;
  }, [productName]);

  useEffect(() => {
    const move = !first.current;
    first.current = false;
    const apply = (h1: HTMLElement | null) => {
      const title = h1?.textContent?.trim();
      document.title = title ? `${title} — ${nameRef.current}` : nameRef.current;
      if (move) (h1 ?? document.getElementById("main"))?.focus({ preventScroll: false });
    };
    const h1 = document.querySelector<HTMLElement>("main h1");
    if (h1) {
      apply(h1);
      return;
    }
    // Not rendered yet (a lazy route): move focus to main now so the reader is not stranded, then to the h1 when it lands.
    if (move) document.getElementById("main")?.focus({ preventScroll: false });
    document.title = nameRef.current;
    const main = document.getElementById("main");
    if (!main) return;
    const observer = new MutationObserver(() => {
      const found = main.querySelector<HTMLElement>("h1");
      if (!found) return;
      observer.disconnect();
      apply(found);
    });
    observer.observe(main, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pathname]);

  // The name changing re-titles the page and nothing else.
  useEffect(() => {
    const title = document.querySelector<HTMLElement>("main h1")?.textContent?.trim();
    document.title = title ? `${title} — ${productName}` : productName;
  }, [productName]);

  return null;
}
